const sectionSelector = '[class*="shopify-section-group-header-"].announcement-bar-section';
    const sliderSelector = sectionSelector + ' [data-announcement-slider]';
    const slideSelector = '[data-announcement-slide]';
    const transparentTargetSelector = '[data-transparent-header]';
    const hiddenClass = 'is-hidden-over-transparent';
    const controllers = new WeakMap();
    let editorCleanup = null;
    let initialized = false;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const getTransparentTargets = (announcementSection) => {
      return Array.from(document.querySelectorAll(transparentTargetSelector)).filter((target) => {
        if (announcementSection.contains(target)) return false;

        const styles = window.getComputedStyle(target);
        return styles.display !== 'none' && styles.visibility !== 'hidden';
      });
    };

    const createVisibilityController = (sliderElement) => {
      const announcementSection = sliderElement.closest(sectionSelector);

      if (!announcementSection) return { refresh() {}, update() {}, destroy() {}};
      
      const HIDE_OVERLAP_THRESHOLD = 12;
      const SHOW_OVERLAP_THRESHOLD = 2;
      const HEADER_GROUP_UPDATE_EVENT = "header-group:offsets:update";
      let transparentTargets = [];
      let resizeObserver = null;
      let expandedHeight = 0;
      let isHidden = announcementSection.classList.contains(hiddenClass);

      const updateExpandedHeight = () => {
        const height = Math.ceil(sliderElement.getBoundingClientRect().height);
        if (height <= 0) return;
        expandedHeight = height;
        announcementSection.style.setProperty("--announcement-bar-expanded-height", `${height}px`);
      };

      const requestHeaderGroupUpdate = () => {
        window.requestAnimationFrame(() => document.dispatchEvent(new CustomEvent(HEADER_GROUP_UPDATE_EVENT)));
      };

      const setHiddenState = (nextHiddenState) => {
        if (nextHiddenState === isHidden) return;
      
        isHidden = nextHiddenState;
        announcementSection.classList.toggle(hiddenClass, nextHiddenState);
        announcementSection.setAttribute("aria-hidden", String(nextHiddenState));
        requestHeaderGroupUpdate();
      };

      const getDetectionRect = () => {
        const sectionRect = announcementSection.getBoundingClientRect();
        const detectionHeight = expandedHeight || Math.ceil(sliderElement.getBoundingClientRect().height);

        return {
          top: sectionRect.top,
          bottom: sectionRect.top + detectionHeight,
          left: sectionRect.left,
          right: sectionRect.right,
          width: sectionRect.width,
          height: detectionHeight,
        };
      };

      const getOverlap = (announcementRect, targetRect) => {
        const horizontalOverlap = Math.min(announcementRect.right, targetRect.right) - Math.max(announcementRect.left, targetRect.left);
        if (horizontalOverlap <= 0) return 0;
      
        const verticalOverlap = Math.min(announcementRect.bottom, targetRect.bottom) - Math.max(announcementRect.top, targetRect.top);
      
        return Math.max(0, Math.round(verticalOverlap));
      };

      const updateVisibility = () => {
        const transparentHeader = document.querySelector('sticky-header[data-transparent-enabled="true"]');

        if (!transparentHeader) {
          setHiddenState(false);
          return;
        }

        updateExpandedHeight();
        const detectionRect = getDetectionRect();

        if (detectionRect.width <= 0 || detectionRect.height <= 0) {
          setHiddenState(false);
          return;
        }

        const maximumOverlap = transparentTargets.reduce((maximum, target) => {
          const targetRect = target.getBoundingClientRect();
          return Math.max(maximum, getOverlap(detectionRect, targetRect));
        }, 0);

        if (isHidden) {
          if (maximumOverlap <= SHOW_OVERLAP_THRESHOLD) setHiddenState(false);
          return;
        }

        if (maximumOverlap >= HIDE_OVERLAP_THRESHOLD) setHiddenState(true);
      };

      const requestUpdate = updateVisibility;
      const refreshTargets = () => {
        updateExpandedHeight();
        transparentTargets = getTransparentTargets(announcementSection);
        resizeObserver?.disconnect();

        if (typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(requestUpdate);
          resizeObserver.observe(sliderElement);
          transparentTargets.forEach((target) => resizeObserver.observe(target));
        }
        requestUpdate();
      };
      window.addEventListener("scroll", requestUpdate, { passive: true });
      document.addEventListener('header-group:layout-ready', requestUpdate);
      refreshTargets();

      return {
        refresh: refreshTargets,
        update: requestUpdate,
        destroy() {
          window.removeEventListener("scroll", requestUpdate);
          document.removeEventListener('header-group:layout-ready',requestUpdate);
          resizeObserver?.disconnect();
          announcementSection.style.removeProperty("--announcement-bar-expanded-height");
          setHiddenState(false);
        }
      };
    };

    const initializeAnnouncementSlider = (sliderElement) => {
      const existingInstance = window.swiperFunctions.getInstance(sliderElement);

      if (existingInstance) return existingInstance;

      const slides = Array.from(sliderElement.querySelectorAll(slideSelector));

      if (!slides.length) return null;

      const hasMultipleSlides = slides.length > 1;
      const autoplayEnabled = sliderElement.dataset.autoplay === 'true' && hasMultipleSlides;
      const autoplaySpeed = Number.parseInt(sliderElement.dataset.autoplaySpeed, 10) || 5000;
      const visibilityController = createVisibilityController(sliderElement);

      const instance = window.swiperFunctions.init(sliderElement, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: prefersReducedMotion ? 0 : 450,
        autoHeight: false,
        watchOverflow: true,
        allowTouchMove: hasMultipleSlides,
        simulateTouch: hasMultipleSlides,
        grabCursor: hasMultipleSlides,
        loop: hasMultipleSlides,
        rewind: false,
        keyboard: {
          enabled: hasMultipleSlides,
          onlyInViewport: true,
        },
        autoplay: autoplayEnabled && !prefersReducedMotion ? {
          delay: autoplaySpeed,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
          waitForTransition: true,
        } : false,
        onResize() {
          visibilityController.update();
        },
        on: {
          init(swiperInstance) {
            swiperInstance.updateAutoHeight?.(0);
            visibilityController.refresh();
          },

          slideChangeTransitionEnd(swiperInstance) {
            swiperInstance.updateAutoHeight?.(prefersReducedMotion ? 0 : 300);
            visibilityController.update();
          }
        }
      });

      if (!instance) {
        visibilityController.destroy();
        return null;
      }

      controllers.set(sliderElement, { visibilityController });
      visibilityController.refresh();
      return instance;
    };

    const destroyAnnouncementSlider = (sliderElement) => {
      const controller = controllers.get(sliderElement);
      controller?.visibilityController?.destroy();
      window.swiperFunctions.destroy(sliderElement);
      controllers.delete(sliderElement);
    };

    const initializeCurrentSlider = () => {
      const sliderElement = document.querySelector(sliderSelector);
      if (!sliderElement) return null;
      return initializeAnnouncementSlider(sliderElement);
    };

    const initializeModule = () => {
      if (initialized) return;
      initialized = true;
      initializeCurrentSlider();

      editorCleanup = window.swiperFunctions.bindShopifyEditor({
        sliderSelector,
        slideSelector,
        initialize: initializeAnnouncementSlider,
        destroyElement: destroyAnnouncementSlider,
        shouldRestartAutoplay({ sliderElement }) {
          return sliderElement.dataset.autoplay === 'true' && !prefersReducedMotion;
        },
        onSectionLoad(event, sliderElement) {
          if (!sliderElement) return;
          controllers.get(sliderElement)?.visibilityController?.refresh();
        }
      });
    };

    if (window.swiperFunctions) {
      initializeModule();
    } else {
      document.addEventListener('swiper-functions:ready', initializeModule, { once: true });
    }
